<template>
  <div class="ship-list-page">
    <!-- ====== 页头 ====== -->
    <div class="page-header">
      <div class="header-bg">
        <svg
          viewBox="0 0 800 200"
          preserveAspectRatio="none"
          class="header-svg"
        >
          <circle cx="100" cy="100" r="180" fill="rgba(255,255,255,0.05)" />
          <circle cx="650" cy="40" r="150" fill="rgba(8,145,178,0.20)" />
          <circle cx="780" cy="180" r="90" fill="rgba(255,255,255,0.06)" />
          <path
            d="M 0 150 Q 200 100 400 130 T 800 110"
            stroke="rgba(255,255,255,0.10)"
            stroke-width="1"
            fill="none"
          />
          <path
            d="M 0 170 Q 220 140 420 160 T 800 140"
            stroke="rgba(255,255,255,0.05)"
            stroke-width="1"
            fill="none"
          />
        </svg>
        <svg viewBox="0 0 64 64" class="compass-deco" aria-hidden="true">
          <circle
            cx="32"
            cy="32"
            r="28"
            fill="none"
            stroke="rgba(255,255,255,0.18)"
            stroke-width="1.2"
          />
          <circle
            cx="32"
            cy="32"
            r="20"
            fill="none"
            stroke="rgba(255,255,255,0.10)"
            stroke-width="1"
          />
          <circle cx="32" cy="32" r="3" fill="rgba(255,255,255,0.8)" />
          <g
            v-for="i in 8"
            :key="i"
            :transform="`rotate(${(i - 1) * 45} 32 32)`"
          >
            <rect
              x="31.2"
              y="6"
              width="1.6"
              height="6"
              fill="rgba(255,255,255,0.45)"
              rx="0.6"
            />
          </g>
          <polygon points="32,10 34,32 32,32" fill="rgba(255,255,255,0.85)" />
          <polygon points="32,54 30,32 32,32" fill="rgba(165,243,252,0.55)" />
        </svg>
      </div>
      <div class="title-block">
        <div class="hero-tag">RACERN · FLEET</div>
        <h1 class="page-title">{{ $t("r.ship_list") }}</h1>
        <p class="page-desc">
          跨集团检索全部船舶，实时掌握在线状态、终端绑定与授权情况
        </p>
      </div>
      <div class="header-actions">
        <span v-if="lastRefreshTime" class="last-refresh">
          <span class="live-dot"></span>
          上次刷新 · {{ lastRefreshTime }}
        </span>
        <button
          class="refresh-btn"
          :class="{ loading }"
          :disabled="loading"
          @click="fetchShipList"
        >
          <span class="ripple"></span>
          <el-icon class="refresh-icon"><Refresh /></el-icon>
          <span>刷新</span>
        </button>
      </div>
    </div>

    <!-- ====== 统计卡 ====== -->
    <div class="stat-row">
      <div class="stat-card stat-total">
        <div class="stat-card-glow"></div>
        <div class="stat-card-top">
          <div class="stat-icon-ring">
            <span class="ring-progress" :style="totalProgressStyle"></span>
            <div class="stat-icon">
              <el-icon><Ship /></el-icon>
            </div>
          </div>
          <span class="stat-badge">FLEET</span>
        </div>
        <div class="stat-body">
          <div class="stat-label">船舶总数</div>
          <div class="stat-value">
            {{ stats.shipTotal }}<span class="stat-unit">艘</span>
          </div>
          <div class="stat-tip">全部集团的船舶数量</div>
        </div>
      </div>

      <div class="stat-card stat-online">
        <div class="stat-card-glow"></div>
        <div class="stat-card-top">
          <div class="stat-icon-ring">
            <span class="ring-pulse"></span>
            <div class="stat-icon">
              <el-icon><Compass /></el-icon>
            </div>
          </div>
          <span class="stat-badge live">LIVE</span>
        </div>
        <div class="stat-body">
          <div class="stat-label">在线船舶</div>
          <div class="stat-value">
            {{ stats.onlineTotal }}<span class="stat-unit">艘</span>
          </div>
          <div class="stat-tip">全量统计 · 在线率 {{ onlineRate }}%</div>
        </div>
      </div>

      <div class="stat-card stat-offline">
        <div class="stat-card-glow"></div>
        <div class="stat-card-top">
          <div class="stat-icon-ring">
            <div class="stat-icon">
              <el-icon><CircleClose /></el-icon>
            </div>
          </div>
          <span class="stat-badge muted">OFF</span>
        </div>
        <div class="stat-body">
          <div class="stat-label">离线船舶</div>
          <div class="stat-value">
            {{ stats.offlineTotal }}<span class="stat-unit">艘</span>
          </div>
          <div class="stat-tip">全量统计</div>
        </div>
      </div>

      <div class="stat-card stat-authorized">
        <div class="stat-card-glow"></div>
        <div class="stat-card-top">
          <div class="stat-icon-ring">
            <div class="stat-icon">
              <el-icon><View /></el-icon>
            </div>
          </div>
          <span class="stat-badge">已授权</span>
        </div>
        <div class="stat-body">
          <div class="stat-label">已授权船舶</div>
          <div class="stat-value">
            {{ authorizedTotal }}<span class="stat-unit">艘</span>
          </div>
          <div class="stat-tip">全表有效授权去重计数</div>
        </div>
      </div>
    </div>

    <!-- ====== 工具条 ====== -->
    <div class="toolbar">
      <el-select
        v-model="queryOrgId"
        class="org-select"
        placeholder="选择集团"
        clearable
        @change="handleOrgChange"
      >
        <el-option label="全部集团" value="" />
        <el-option
          v-for="org in orgList"
          :key="org.id"
          :label="org.name"
          :value="org.id"
        />
      </el-select>
      <el-input
        v-model="queryName"
        class="query-input"
        placeholder="搜索船舶名称…"
        clearable
        @keyup.enter="handleSearch"
        @clear="handleSearch"
      >
        <template #prefix
          ><el-icon><Search /></el-icon
        ></template>
      </el-input>
      <el-select
        v-model="onlineFilter"
        class="filter-select"
        placeholder="在线状态"
        clearable
        @change="handleSearch"
      >
        <el-option label="全部" value="" />
        <el-option label="在线" value="online" />
        <el-option label="离线" value="offline" />
      </el-select>
      <el-select
        v-model="authFilter"
        class="filter-select"
        placeholder="授权状态"
        clearable
        @change="handleSearch"
      >
        <el-option label="全部" value="" />
        <el-option label="已授权" value="authorized" />
        <el-option label="授权中" value="authorizing" />
        <el-option label="未授权" value="unauthorized" />
      </el-select>
      <el-button
        type="primary"
        class="search-btn"
        :loading="loading"
        @click="handleSearch"
        >搜索</el-button
      >
      <el-button class="reset-btn" @click="handleReset">重置</el-button>
      <div class="toolbar-spacer"></div>
      <div class="result-tag">
        <span class="result-dot"></span>
        当前页 {{ shipList.length }} 艘 / 共 {{ page.total }} 艘
      </div>
    </div>

    <!-- ====== 船队卡片网格 ====== -->
    <div v-loading="loading" class="fleet-section">
      <div v-if="shipList.length === 0" class="empty-state">
        <svg viewBox="0 0 120 120" class="empty-svg" aria-hidden="true">
          <circle
            cx="60"
            cy="60"
            r="40"
            fill="none"
            stroke="#cbd5e1"
            stroke-width="2"
          />
          <line
            x1="40"
            y1="40"
            x2="80"
            y2="80"
            stroke="#94a3b8"
            stroke-width="2"
          />
        </svg>
        <div class="empty-title">暂无符合条件的船舶</div>
        <div class="empty-desc">调整搜索或筛选条件后重新查询</div>
      </div>

      <div v-else class="ship-grid">
        <div v-for="row in shipList" :key="row.ship.id" class="ship-card">
          <div class="ship-card-top"></div>
          <div class="ship-card-head">
            <div class="card-avatar">
              <el-icon><Ship /></el-icon>
              <span
                class="online-dot"
                :class="{ show: row.status?.id === 1 }"
              ></span>
            </div>
            <div class="card-title">
              <div class="card-name" :title="row.ship.name">
                {{ row.ship.name }}
              </div>
              <div class="card-org">
                <el-icon class="org-icon"><OfficeBuilding /></el-icon>
                <span>{{ row.orgName ?? "未关联集团" }}</span>
              </div>
            </div>
            <span
              class="status-pill"
              :class="row.status?.id === 1 ? 'online' : 'offline'"
            >
              <span class="status-dot"></span>{{ row.status?.cname ?? "-" }}
            </span>
          </div>

          <div class="ship-card-body">
            <div class="auth-status-row">
              <span class="info-label">授权状态</span>
              <span
                class="auth-status"
                :class="authStatusClass(row.authorization)"
              >
                {{ authStatusText(row.authorization) }}
              </span>
            </div>

            <div class="auth-block">
              <span class="info-label">授权时段</span>
              <div v-if="row.authorization" class="auth-time">
                <span class="time-chip">{{
                  formatDate(row.authorization.startTime)
                }}</span>
                <el-icon class="time-arrow"><ArrowRight /></el-icon>
                <span class="time-chip">{{
                  formatDate(row.authorization.endTime)
                }}</span>
              </div>
              <span v-else class="muted-text">未授权</span>
            </div>

            <div class="info-item create-time">
              <span class="info-label">创建时间</span>
              <span class="info-value">{{ row.ship.createtime || "—" }}</span>
            </div>
          </div>

          <div class="ship-card-foot">
            <!-- 辅助操作收纳到"更多"下拉，腾出位置给主按钮 -->
            <el-dropdown
              trigger="click"
              :hide-on-click="true"
              popper-class="ship-card-dropdown"
            >
              <button class="action-chip action-more" type="button">
                <span>更多</span>
                <el-icon class="caret"><CaretBottom /></el-icon>
              </button>
              <template #dropdown>
                <el-dropdown-menu class="ship-card-dropdown">
                  <el-dropdown-item @click="goProtocolFile(row)">
                    <el-icon><Document /></el-icon>
                    <span>终端文件</span>
                  </el-dropdown-item>
                  <!-- 参数配置入口禁用（待开发）：需要恢复"不可点击"时，
                       删除下方启用项，取消注释此块即可
                  <el-dropdown-item
                    disabled
                    title="待开发"
                    @click="goCalcParam(row)"
                  >
                    <el-icon><Setting /></el-icon>
                    <span>参数配置</span>
                  </el-dropdown-item>
                  -->
                  <el-dropdown-item @click="goCalcParam(row)">
                    <el-icon><Setting /></el-icon>
                    <span>参数配置</span>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>

            <!-- 主操作：查看详情 / 获取授权 -->
            <template v-if="row.authorization">
              <el-tooltip
                :disabled="row.authorization.status === 1"
                content="授权申请待客户确认，或已被拒绝/过期"
                placement="top"
              >
                <button
                  class="action-chip action-detail"
                  :class="{ disabled: row.authorization.status !== 1 }"
                  :disabled="row.authorization.status !== 1"
                  @click="goShipInfo(row)"
                >
                  <el-icon><View /></el-icon>
                  <span>查看详情</span>
                  <el-icon class="arrow"><ArrowRight /></el-icon>
                </button>
              </el-tooltip>
            </template>
            <button
              v-else
              class="action-chip action-apply"
              @click="openApplyAuthorization(row)"
            >
              <el-icon><Key /></el-icon>
              <span>获取授权</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="pagination-bar">
      <el-pagination
        v-model:current-page="page.current"
        v-model:page-size="page.size"
        :total="page.total"
        :page-sizes="[10, 20, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        background
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </div>

    <!-- ====== 授权申请弹窗 ====== -->
    <el-dialog
      v-model="applyDialogVisible"
      class="apply-dialog"
      title="申请船舶授权"
      width="540"
      :before-close="handleApplyDialogClose"
    >
      <div v-if="applyResult === 1" class="apply-result">
        <el-result icon="success" title="提交成功" sub-title="等待用户授权">
          <template #extra
            ><el-button type="primary" @click="closeApplyDialog"
              >关闭窗口</el-button
            ></template
          >
        </el-result>
      </div>
      <div v-else-if="applyResult === 0" class="apply-result">
        <el-result
          icon="error"
          title="提交失败"
          sub-title="请检查填写信息后重新提交"
        />
      </div>
      <div v-else class="apply-form">
        <div class="apply-ship-name">
          <div class="apply-ship-icon">
            <el-icon><Ship /></el-icon>
          </div>
          <div>
            <div class="title-text">{{ selectedShip?.name }}</div>
            <div class="title-sub">为这艘船舶申请访问授权</div>
          </div>
        </div>
        <el-form label-width="92">
          <el-form-item label="授权类型">
            <el-select
              v-model="applyParams.authorityId"
              placeholder="请选择授权类型"
              style="width: 100%"
            >
              <el-option
                v-for="item in shipAuthorityList"
                :key="item.id"
                :label="getName(item)"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="授权时段">
            <el-date-picker
              v-model="datetimeRange"
              type="datetimerange"
              :shortcuts="shortcuts"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              style="width: 100%"
            />
          </el-form-item>
          <el-form-item label="申请缘由">
            <el-input
              v-model="applyParams.description"
              type="textarea"
              :rows="4"
              resize="none"
              placeholder="请在此处填写申请缘由，便于客户更好地理解权限用途"
            />
          </el-form-item>
          <el-button
            type="primary"
            class="submit-btn"
            :loading="submitting"
            style="width: 100%"
            @click="submitApply"
            >提交申请</el-button
          >
        </el-form>
      </div>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useRouter } from "vue-router";
import {
  ArrowRight,
  CaretBottom,
  CircleClose,
  Compass,
  Document,
  Key,
  OfficeBuilding,
  Refresh,
  Search,
  Setting,
  Ship,
  View,
} from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import {
  getShipAuthorityListApi,
  getShipListApi,
  submitShipAuthorizationApplyApi,
} from "@/api/productApi";
import { fetchOrganizationListApi } from "@/api/organization/organizationApi";
import type {
  ApplyShipAuthorizationParams,
  Ship as ShipEntity,
  ShipListItem,
} from "@/types/product";
import type { Authority } from "@/types/resource";
import type { Organization } from "@/types/organization";
import { getName } from "@/util/i18nUtil";

const router = useRouter();

const loading = ref<boolean>(false);
const submitting = ref<boolean>(false);
const queryName = ref<string>("");
const queryOrgId = ref<string>("");
const onlineFilter = ref<string>("");
const authFilter = ref<string>("");
const orgList = ref<Organization[]>([]);
const shipList = ref<ShipListItem[]>([]);
const authorizedTotal = ref<number>(0);
const lastRefreshTime = ref<string>("");
const page = reactive({ current: 1, size: 10, total: 0 });

/**
 * 全量统计（统计卡片专用）
 * 后端基于名称/集团条件返回全量口径统计，不受在线/授权筛选影响
 */
const stats = reactive({ shipTotal: 0, onlineTotal: 0, offlineTotal: 0 });

/**
 * 全量在线率（在线船舶 / 船舶总数）
 */
const onlineRate = computed(() =>
  stats.shipTotal === 0
    ? 0
    : Math.round((stats.onlineTotal / stats.shipTotal) * 100)
);

const totalProgressStyle = computed(() => ({
  background: `conic-gradient(#1e3799 ${Math.min(
    onlineRate.value,
    100
  )}%, rgba(30, 55, 153, 0.12) 0)`,
}));

const authStatusText = (auth: ShipListItem["authorization"]) => {
  if (!auth) return "未授权";
  if (auth.status === 1) return "已授权";
  return "授权中";
};

const authStatusClass = (auth: ShipListItem["authorization"]) => {
  if (!auth) return "auth-red";
  if (auth.status === 1) return "auth-green";
  return "auth-yellow";
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("zh-CN", { hour12: false });

const fetchShipList = async () => {
  loading.value = true;
  const result = await getShipListApi({
    current: page.current,
    size: page.size,
    name: queryName.value || undefined,
    orgId: queryOrgId.value || undefined,
    onlineFilter: onlineFilter.value || undefined,
    authFilter: authFilter.value || undefined,
  });
  if (result) {
    shipList.value = result.page.records;
    page.total = result.page.total;
    authorizedTotal.value = result.authorizedTotal;
    stats.shipTotal = result.shipTotal;
    stats.onlineTotal = result.onlineTotal;
    stats.offlineTotal = result.offlineTotal;
    lastRefreshTime.value = new Date().toLocaleTimeString();
  } else {
    ElMessage.error("船舶列表加载失败");
  }
  loading.value = false;
};

const fetchOrganizations = async () => {
  const list = await fetchOrganizationListApi({
    name: "",
    page: 1,
    size: 1000,
  });
  if (list) orgList.value = list;
};

const handleOrgChange = () => {
  page.current = 1;
  fetchShipList();
};

const handleSearch = () => {
  page.current = 1;
  fetchShipList();
};

const handleReset = () => {
  queryName.value = "";
  queryOrgId.value = "";
  onlineFilter.value = "";
  authFilter.value = "";
  page.current = 1;
  fetchShipList();
};

const handlePageChange = (current: number) => {
  page.current = current;
  fetchShipList();
};

const handleSizeChange = (size: number) => {
  page.size = size;
  page.current = 1;
  fetchShipList();
};

const goProtocolFile = (row: ShipListItem) => {
  router.push({ path: "/protocol_file", query: { shipId: row.ship.id } });
};

const goCalcParam = (row: ShipListItem) => {
  router.push({ name: "shipCalcParam", query: { shipId: row.ship.id } });
};

const goShipInfo = (row: ShipListItem) => {
  router.push({ name: "shipInfo", query: { shipId: row.ship.id } });
};

const applyDialogVisible = ref<boolean>(false);
const applyResult = ref<-1 | 0 | 1>(-1);
const selectedShip = ref<ShipEntity | undefined>(undefined);
const datetimeRange = ref<[Date, Date] | []>([]);
const shipAuthorityList = ref<Authority[]>([]);
const applyParams = reactive<ApplyShipAuthorizationParams>({
  shipId: "",
  orgId: "",
  startTime: "",
  endTime: "",
  description: "",
  authorityId: null,
});

const shortcuts = [
  {
    text: "三小时",
    value: () => {
      const start = new Date();
      const end = new Date();
      end.setHours(end.getHours() + 3);
      return [start, end];
    },
  },
  {
    text: "一天",
    value: () => {
      const start = new Date();
      const end = new Date();
      end.setDate(end.getDate() + 1);
      return [start, end];
    },
  },
  {
    text: "一星期",
    value: () => {
      const start = new Date();
      const end = new Date();
      end.setDate(end.getDate() + 7);
      return [start, end];
    },
  },
  {
    text: "一个月",
    value: () => {
      const start = new Date();
      const end = new Date();
      end.setMonth(end.getMonth() + 1);
      return [start, end];
    },
  },
];

const openApplyAuthorization = async (row: ShipListItem) => {
  selectedShip.value = row.ship;
  applyParams.shipId = row.ship.id;
  applyParams.orgId = row.ship.orgId ?? "";
  applyParams.authorityId = null;
  applyParams.description = "";
  applyParams.startTime = "";
  applyParams.endTime = "";
  datetimeRange.value = [];
  applyResult.value = -1;
  if (shipAuthorityList.value.length === 0) {
    const list = await getShipAuthorityListApi();
    if (list) shipAuthorityList.value = list;
  }
  applyDialogVisible.value = true;
};

const submitApply = async () => {
  if (!applyParams.authorityId) {
    ElMessage.warning("请选择授权类型");
    return;
  }
  if (!Array.isArray(datetimeRange.value) || datetimeRange.value.length !== 2) {
    ElMessage.warning("请选择授权时段");
    return;
  }
  const [start, end] = datetimeRange.value as [Date, Date];
  if (end <= start) {
    ElMessage.warning("结束时间需晚于开始时间");
    return;
  }
  applyParams.startTime = start.toISOString();
  applyParams.endTime = end.toISOString();

  submitting.value = true;
  const response = await submitShipAuthorizationApplyApi({ ...applyParams });
  submitting.value = false;

  if (response != null) {
    applyResult.value = 1;
    ElMessage.success("授权申请已提交，等待用户确认");
    fetchShipList();
  } else {
    applyResult.value = 0;
    ElMessage.error("授权申请提交失败，请重试");
  }
};

const closeApplyDialog = () => {
  applyDialogVisible.value = false;
};

const handleApplyDialogClose = (done: () => void) => {
  done();
  setTimeout(() => {
    selectedShip.value = undefined;
    applyResult.value = -1;
    datetimeRange.value = [];
    applyParams.authorityId = null;
    applyParams.description = "";
    applyParams.startTime = "";
    applyParams.endTime = "";
  }, 300);
};

onMounted(() => {
  fetchOrganizations();
  fetchShipList();
});
</script>

<style lang="less" scoped>
.ship-list-page {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px 28px 32px;
  box-sizing: border-box;
  min-height: 100vh;
  background: radial-gradient(
      at 10% 0%,
      rgba(8, 145, 178, 0.06) 0px,
      transparent 45%
    ),
    radial-gradient(at 90% 100%, rgba(13, 148, 136, 0.05) 0px, transparent 45%),
    linear-gradient(180deg, #f4f8fb 0%, #e7eef5 100%);
  background-attachment: fixed;
}

/* ===== 页头 ===== */
.page-header {
  position: relative;
  background: linear-gradient(135deg, #0c2461 0%, #1e3799 45%, #0a3d62 100%);
  color: #fff;
  padding: 48px 44px;
  border-radius: 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 24px;
  min-height: 220px;
  overflow: hidden;
  box-sizing: border-box;
  box-shadow: 0 16px 36px rgba(12, 36, 97, 0.32);

  .header-bg {
    position: absolute;
    inset: 0;
    pointer-events: none;

    .header-svg {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
    }

    .compass-deco {
      position: absolute;
      right: -40px;
      top: 50%;
      width: 320px;
      height: 320px;
      transform: translateY(-50%);
      opacity: 0.32;
      animation: spinSlow 50s linear infinite;
    }
  }

  .title-block {
    position: relative;
    z-index: 1;
    flex: 1 1 520px;
    min-width: 0;

    .hero-tag {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 5px 14px;
      font-size: 11px;
      letter-spacing: 3px;
      font-weight: 600;
      color: #a5f3fc;
      background: rgba(165, 243, 252, 0.14);
      border: 1px solid rgba(165, 243, 252, 0.32);
      border-radius: 999px;
      margin-bottom: 18px;

      &::before {
        content: "";
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #67e8f9;
        box-shadow: 0 0 8px #67e8f9;
      }
    }

    .page-title {
      font-size: 36px;
      font-weight: 800;
      color: #fff;
      margin: 0 0 14px;
      letter-spacing: 1px;
      text-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
      line-height: 1.25;
    }

    .page-desc {
      color: rgba(224, 242, 254, 0.88);
      font-size: 14.5px;
      margin: 0;
      letter-spacing: 0.4px;
      line-height: 1.8;
      max-width: 640px;
    }
  }

  .header-actions {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 0 0 auto;

    .last-refresh {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      color: rgba(224, 242, 254, 0.9);
      padding: 5px 12px;
      background: rgba(255, 255, 255, 0.12);
      border: 1px solid rgba(255, 255, 255, 0.18);
      border-radius: 999px;
      backdrop-filter: blur(8px);

      .live-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: #34d399;
        box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.7);
        animation: livePulse 1.8s ease-out infinite;
      }
    }

    .refresh-btn {
      position: relative;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 10px 22px;
      border: 1px solid rgba(255, 255, 255, 0.28);
      border-radius: 10px;
      background: rgba(255, 255, 255, 0.16);
      color: #fff;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      overflow: hidden;
      backdrop-filter: blur(8px);
      transition: transform 0.2s ease, background 0.3s ease;

      .ripple {
        position: absolute;
        inset: 0;
        background: radial-gradient(
          circle at center,
          rgba(255, 255, 255, 0.4) 0%,
          transparent 70%
        );
        opacity: 0;
        transform: scale(0.5);
        transition: opacity 0.4s ease, transform 0.6s ease;
        pointer-events: none;
      }

      .refresh-icon {
        font-size: 15px;
        transition: transform 0.5s ease;
      }

      &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.22);
        transform: translateY(-1px);

        .ripple {
          opacity: 1;
          transform: scale(1.5);
        }
      }

      &.loading .refresh-icon {
        animation: spin 1s linear infinite;
      }

      &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
      }
    }
  }
}

/* ===== 统计卡 ===== */
.stat-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}

.stat-card {
  position: relative;
  background: #fff;
  border-radius: 16px;
  padding: 18px 20px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02);
  overflow: hidden;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: currentColor;
    opacity: 0.85;
  }

  .stat-card-glow {
    position: absolute;
    top: -60px;
    right: -60px;
    width: 180px;
    height: 180px;
    background: radial-gradient(circle, currentColor 0%, transparent 70%);
    opacity: 0.06;
    border-radius: 50%;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 14px 30px rgba(0, 0, 0, 0.08), 0 4px 8px rgba(0, 0, 0, 0.04);

    .stat-card-glow {
      opacity: 0.16;
    }

    .stat-icon-ring {
      transform: scale(1.05) rotate(-3deg);
    }
  }

  &.stat-total {
    color: #1e3799;
  }
  &.stat-online {
    color: #0891b2;
  }
  &.stat-offline {
    color: #64748b;
  }
  &.stat-authorized {
    color: #059669;
  }

  .stat-card-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 14px;
  }

  .stat-icon-ring {
    position: relative;
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.6);
    box-shadow: inset 0 0 0 1px currentColor;
    transition: transform 0.3s ease;

    .ring-progress {
      position: absolute;
      inset: -3px;
      border-radius: 16px;
      z-index: -1;
      opacity: 0.35;
    }

    .ring-pulse {
      position: absolute;
      inset: -4px;
      border-radius: 16px;
      background: currentColor;
      opacity: 0.25;
      animation: ringPulse 1.8s ease-out infinite;
    }

    .stat-icon {
      font-size: 22px;
      color: currentColor;
    }
  }

  .stat-badge {
    display: inline-block;
    padding: 2px 8px;
    font-size: 9px;
    letter-spacing: 2px;
    font-weight: 700;
    border-radius: 4px;
    color: currentColor;
    background: rgba(255, 255, 255, 0.5);
    box-shadow: inset 0 0 0 1px currentColor;

    &.live {
      animation: badgePulse 1.8s ease-out infinite;
    }

    &.muted {
      opacity: 0.6;
    }
  }

  .stat-body {
    .stat-label {
      font-size: 13px;
      color: #64748b;
      letter-spacing: 0.3px;
      margin-bottom: 4px;
    }

    .stat-value {
      font-size: 28px;
      font-weight: 800;
      color: #0f172a;
      line-height: 1.1;
      font-variant-numeric: tabular-nums;

      .stat-unit {
        font-size: 13px;
        font-weight: 500;
        color: #94a3b8;
        margin-left: 4px;
      }
    }

    .stat-tip {
      font-size: 11px;
      color: #94a3b8;
      margin-top: 6px;
      letter-spacing: 0.2px;
    }
  }
}

/* ===== 工具条 ===== */
.toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(8, 145, 178, 0.12);
  border-radius: 14px;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 14px rgba(8, 145, 178, 0.08);

  .query-input {
    width: 280px;

    :deep(.el-input__wrapper) {
      border-radius: 8px;
      box-shadow: 0 0 0 1px rgba(8, 145, 178, 0.18);
      transition: box-shadow 0.2s ease;
    }

    :deep(.el-input__wrapper.is-focus) {
      box-shadow: 0 0 0 1px #0891b2, 0 0 0 4px rgba(8, 145, 178, 0.15);
    }
  }

  .org-select {
    width: 200px;

    :deep(.el-select__wrapper) {
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.85);
      box-shadow: 0 0 0 1px rgba(8, 145, 178, 0.18) !important;
      transition: box-shadow 0.2s ease;
    }

    :deep(.el-select__wrapper.is-focused) {
      box-shadow: 0 0 0 1px #0891b2, 0 0 0 4px rgba(8, 145, 178, 0.15);
    }
  }

  .filter-select {
    width: 130px;

    :deep(.el-select__wrapper) {
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.85);
      box-shadow: 0 0 0 1px rgba(8, 145, 178, 0.18) !important;
      transition: box-shadow 0.2s ease;
    }

    :deep(.el-select__wrapper.is-focused) {
      box-shadow: 0 0 0 1px #0891b2, 0 0 0 4px rgba(8, 145, 178, 0.15);
    }
  }

  .search-btn {
    border-radius: 8px;
    background: linear-gradient(135deg, #0e7490 0%, #0891b2 100%);
    border: none;
    box-shadow: 0 4px 12px rgba(8, 145, 178, 0.28);
    transition: transform 0.2s ease, box-shadow 0.3s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(8, 145, 178, 0.36);
    }
  }

  .reset-btn {
    border-radius: 8px;
  }

  .toolbar-spacer {
    flex: 1;
  }

  .result-tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    font-size: 12px;
    color: #0e7490;
    background: rgba(8, 145, 178, 0.1);
    border-radius: 999px;
    font-weight: 600;

    .result-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #0891b2;
      box-shadow: 0 0 6px rgba(8, 145, 178, 0.6);
    }
  }
}

/* ===== 船队卡片网格 ===== */
.fleet-section {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.ship-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}

.ship-card {
  position: relative;
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02),
    0 4px 16px rgba(8, 145, 178, 0.04);
  border: 1px solid rgba(8, 145, 178, 0.08);
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #0e7490 0%, #0891b2 50%, #67e8f9 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 32px rgba(8, 145, 178, 0.12),
      0 4px 8px rgba(0, 0, 0, 0.04);

    &::before {
      opacity: 1;
    }
  }

  .ship-card-top {
    display: none;
  }

  .ship-card-head {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .card-avatar {
    position: relative;
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: linear-gradient(135deg, #0c2461 0%, #0891b2 100%);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    box-shadow: 0 4px 12px rgba(8, 145, 178, 0.28);

    .online-dot {
      position: absolute;
      right: -3px;
      bottom: -3px;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: #cbd5e1;
      border: 2px solid #fff;
      opacity: 0;
      transform: scale(0.6);
      transition: opacity 0.3s ease, transform 0.3s ease;

      &.show {
        background: #10b981;
        opacity: 1;
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.6);
        animation: livePulse 1.8s ease-out infinite;
      }
    }
  }

  .card-title {
    flex: 1;
    min-width: 0;

    .card-name {
      font-size: 16px;
      font-weight: 700;
      color: #0f172a;
      line-height: 1.3;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .card-org {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: #94a3b8;
      margin-top: 4px;

      .org-icon {
        font-size: 12px;
      }

      > span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 180px;
      }
    }
  }

  .status-pill {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;

    .status-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
    }

    &.online {
      color: #047857;
      background: rgba(16, 185, 129, 0.14);

      .status-dot {
        background: #10b981;
      }
    }

    &.offline {
      color: #64748b;
      background: rgba(100, 116, 139, 0.14);

      .status-dot {
        background: #94a3b8;
      }
    }
  }

  .ship-card-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-top: 4px;
    border-top: 1px dashed rgba(8, 145, 178, 0.12);
    padding-top: 14px;
  }

  .info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 16px;
  }

  .auth-status-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .create-time {
    padding-top: 8px;
    border-top: 1px dashed rgba(8, 145, 178, 0.1);
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;

    .info-label {
      font-size: 11px;
      color: #94a3b8;
      letter-spacing: 0.3px;
    }

    .info-value {
      font-size: 13px;
      color: #1e293b;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .auth-block {
    display: flex;
    flex-direction: column;
    gap: 6px;

    .info-label {
      font-size: 11px;
      color: #94a3b8;
      letter-spacing: 0.3px;
    }
  }

  .auth-time {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;

    .time-chip {
      font-size: 11px;
      color: #475569;
      padding: 3px 8px;
      border-radius: 6px;
      background: rgba(8, 145, 178, 0.08);
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
    }

    .time-arrow {
      font-size: 11px;
      color: #94a3b8;
    }
  }

  .auth-status {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
  }

  .auth-red {
    color: #b91c1c;
    background: rgba(239, 68, 68, 0.12);
  }
  .auth-yellow {
    color: #b45309;
    background: rgba(245, 158, 11, 0.16);
  }
  .auth-green {
    color: #047857;
    background: rgba(16, 185, 129, 0.14);
  }

  .muted-text {
    color: #cbd5e1;
    font-size: 12px;
  }

  .ship-card-foot {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: flex-end;
    align-items: center;
    margin-top: auto;
    padding-top: 16px;
    border-top: 1px dashed rgba(8, 145, 178, 0.14);
  }

  .action-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 10px;
    border: 1px solid transparent;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.3s ease, background 0.3s ease;

    .el-icon {
      font-size: 13px;
    }

    &.action-detail {
      color: #0e7490;
      background: rgba(8, 145, 178, 0.08);
      border-color: rgba(8, 145, 178, 0.2);

      .arrow {
        margin-left: 2px;
        font-size: 12px;
        opacity: 0.7;
        transition: transform 0.2s ease, opacity 0.2s ease;
      }

      &:hover:not(:disabled) {
        color: #fff;
        background: linear-gradient(135deg, #0e7490 0%, #0891b2 100%);
        border-color: transparent;
        box-shadow: 0 4px 12px rgba(8, 145, 178, 0.32);
        transform: translateY(-1px);

        .arrow {
          transform: translateX(2px);
          opacity: 1;
        }
      }

      &.disabled {
        color: #94a3b8;
        background: rgba(148, 163, 184, 0.1);
        border-color: rgba(148, 163, 184, 0.18);
        cursor: not-allowed;
        opacity: 0.65;
      }
    }

    &.action-apply {
      color: #047857;
      background: rgba(16, 185, 129, 0.1);
      border-color: rgba(16, 185, 129, 0.24);

      &:hover {
        color: #fff;
        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        border-color: transparent;
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.32);
        transform: translateY(-1px);
      }
    }

    &.action-terminal {
      color: #6366f1;
      background: rgba(99, 102, 241, 0.1);
      border-color: rgba(99, 102, 241, 0.24);

      &:hover {
        color: #fff;
        background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
        border-color: transparent;
        box-shadow: 0 4px 12px rgba(99, 102, 241, 0.32);
        transform: translateY(-1px);
      }
    }

    &.action-more {
      color: #475569;
      background: #ffffff;
      border-color: rgba(148, 163, 184, 0.32);
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

      .caret {
        margin-left: 2px;
        font-size: 10px;
        opacity: 0.6;
        transition: transform 0.2s ease, opacity 0.2s ease;
      }

      &:hover,
      &:focus {
        color: #0e7490;
        background: var(--p-cyan-soft, #ecfeff);
        border-color: rgba(8, 145, 178, 0.45);
        box-shadow: 0 2px 8px rgba(8, 145, 178, 0.14);

        .caret {
          opacity: 1;
          transform: translateY(1px);
        }
      }
    }
  }
}

.pagination-bar {
  display: flex;
  justify-content: flex-end;
  padding: 14px 18px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid rgba(8, 145, 178, 0.08);
}

/* ===== 空状态 ===== */
.empty-state {
  padding: 40px 0 20px;
  text-align: center;

  .empty-svg {
    width: 120px;
    height: 120px;
    margin-bottom: 12px;
  }

  .empty-title {
    font-size: 15px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 4px;
  }

  .empty-desc {
    font-size: 12px;
    color: #94a3b8;
  }
}

/* ===== 授权申请弹窗 ===== */
.apply-dialog {
  :deep(.el-dialog__header) {
    background: linear-gradient(135deg, #0c2461 0%, #0891b2 100%);
    padding: 20px 24px;
    margin: 0;
  }

  :deep(.el-dialog__title) {
    color: #fff;
    font-weight: 600;
    letter-spacing: 0.5px;
  }

  :deep(.el-dialog__headerbtn .el-dialog__close) {
    color: rgba(255, 255, 255, 0.85);
  }

  :deep(.el-dialog__body) {
    padding: 24px 28px;
  }

  .apply-ship-name {
    display: flex;
    align-items: center;
    gap: 14px;
    padding-bottom: 16px;
    margin-bottom: 20px;
    border-bottom: 1px dashed rgba(8, 145, 178, 0.18);

    .apply-ship-icon {
      flex-shrink: 0;
      width: 44px;
      height: 44px;
      border-radius: 12px;
      background: linear-gradient(135deg, #0e7490 0%, #0891b2 100%);
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 22px;
      box-shadow: 0 4px 12px rgba(8, 145, 178, 0.28);
    }

    .title-text {
      font-size: 16px;
      font-weight: 700;
      color: #0f172a;
    }

    .title-sub {
      font-size: 12px;
      color: #94a3b8;
      margin-top: 2px;
    }
  }

  .submit-btn {
    background: linear-gradient(135deg, #0c2461 0%, #0891b2 100%);
    border: none;
    box-shadow: 0 4px 12px rgba(8, 145, 178, 0.28);
    transition: transform 0.2s ease, box-shadow 0.3s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(8, 145, 178, 0.36);
    }
  }

  .apply-result {
    padding: 8px 0;
  }
}

/* ===== 动画 ===== */
@keyframes livePulse {
  0% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(16, 185, 129, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

@keyframes ringPulse {
  0% {
    transform: scale(1);
    opacity: 0.35;
  }
  100% {
    transform: scale(1.4);
    opacity: 0;
  }
}

@keyframes badgePulse {
  0%,
  100% {
    box-shadow: inset 0 0 0 1px currentColor;
  }
  50% {
    box-shadow: inset 0 0 0 1px currentColor, 0 0 0 4px rgba(8, 145, 178, 0.15);
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes spinSlow {
  from {
    transform: translateY(-50%) rotate(0deg);
  }
  to {
    transform: translateY(-50%) rotate(360deg);
  }
}

/* ===== 响应式 ===== */
@media (max-width: 1200px) {
  .stat-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .stat-row {
    grid-template-columns: 1fr;
  }
  .page-header {
    padding: 22px;
  }
  .toolbar {
    flex-wrap: wrap;
  }
  .toolbar-spacer {
    display: none;
  }
}
</style>

<!-- 下拉菜单样式：popper-append-to-body 默认，下拉浮在 body 上无法被 scoped 捕获，
     用 popper-class 自定义类名 + 此非 scoped 块定义样式（避开数据 v-xxx 属性选择器） -->
<style lang="less">
.ship-card-dropdown.el-dropdown-menu {
  padding: 6px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.24);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.18);
  background: #ffffff;

  .el-dropdown-menu__item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    margin: 2px 0;
    border-radius: 8px;
    font-size: 13px;
    color: #475569;
    line-height: 1.4;
    transition: background 0.2s ease, color 0.2s ease;

    .el-icon {
      font-size: 14px;
      color: #64748b;
    }

    &:not(.is-disabled):hover,
    &:not(.is-disabled):focus {
      background: rgba(8, 145, 178, 0.08);
      color: #0e7490;

      .el-icon {
        color: #0891b2;
      }
    }

    // 待开发禁用项：置灰 + not-allowed 光标
    &.is-disabled {
      color: #a8b3c2;
      cursor: not-allowed;

      .el-icon {
        color: #c3ccd9;
      }
    }
  }
}
</style>
